import { ResourceItem } from '../commonTypes/resourceItem';

export interface TranslatorOptions {
  /** If true, missing keys log a warning (default true in dev, false in production). */
  warnOnMissing?: boolean;
  /** Strategy for missing key: 'empty' | 'default' | 'key'. */
  missingStrategy?: 'empty' | 'default' | 'key';
  /** Optional global default fallback value if strategy = 'default'. */
  globalDefaultValue?: string;
}

const defaultOptions: Required<TranslatorOptions> = {
  warnOnMissing: typeof process !== 'undefined' && process.env.NODE_ENV !== 'production',
  missingStrategy: 'empty',
  globalDefaultValue: '',
};

function buildIndex(translations: ResourceItem[]) {
  const map = new Map<string, string>();
  const normalize = (k: string) => {
    if (!k) return '';
    let n = k.replace(/\//g, '|');
    if (!n.startsWith('|')) n = '|' + n;
    return n.toLowerCase();
  };
  translations.forEach((t) => {
    map.set(normalize(t.Key), t.Value);
  });
  return { map, normalize };
}

function createLookup(translations: ResourceItem[]) {
  const { map, normalize } = buildIndex(translations);
  return (rawKey: string): string | undefined => {
    const norm = normalize(rawKey);
    if (!norm) return undefined;
    // 1. Exact
    if (map.has(norm)) return map.get(norm);
    // 2. Suffix match
    for (const [k, v] of map.entries()) {
      if (k.endsWith(norm)) return v;
    }
    // 3. Regex fallback (escaped original rawKey semantics)
    const escaped = rawKey
      .replace(/\|/g, '\\|')
      .replace(/\//g, '\\|')
      .replace(/[.*+?^${}()\[\]\\]/g, (r) => `\\${r}`);
    const re = new RegExp(`${escaped}$`, 'i');
    for (const [k, v] of map.entries()) {
      if (re.test(k)) return v;
    }
    return undefined;
  };
}

export type translateFunc = ReturnType<typeof makeTranslator>;

export function makeTranslator(translations: ResourceItem[], opts?: TranslatorOptions) {
  const options = { ...defaultOptions, ...opts } as Required<TranslatorOptions>;
  const lookup = createLookup(translations || []);
  return function translate(key: string, defaultValue?: string) {
    if (!translations || translations.length === 0) {
      if (options.warnOnMissing) console.warn('[translator] No translations loaded yet');
      return resolveMissing();
    }
    if (key && key.includes('/')) {
      if (options.warnOnMissing)
        console.warn(`Key "${key}" contains a slash. Prefer '|' to avoid normalization surprises.`);
    }
    const found = lookup(key);
    if (found != null) return found;
    if (options.warnOnMissing) console.warn(`[translator] Missing key: ${key}`);
    return resolveMissing(defaultValue);
  };

  function resolveMissing(localDefault?: string) {
    switch (options.missingStrategy) {
      case 'default':
        return localDefault ?? options.globalDefaultValue ?? '';
      case 'key':
        return localDefault ?? '';
      case 'empty':
      default:
        return '';
    }
  }
}
